import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, Subject } from 'rxjs';
import { Label, RenderPreview } from './buyer-preview.models';

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, inject } from '@angular/core';
import { WINDOW } from '../constants/window';
import { ApiServiceService } from '../services/api-service.service';
import { FormScore } from './scores-analysis/scores-analysis.models';

@Injectable({
  providedIn: 'root',
})
export class BuyerPreviewService {
  labels: Label[] = [
    {
      faIconClass: 'fa fa-check-circle text-success',
      infoText: 'Valid Response',
    },
    {
      faIconClass: 'fa fa-warning text-danger',
      infoText: 'Invalid Response',
    },
    {
      faIconClass: 'fa fa-ban text-disabled',
      infoText: 'Disabled/Non-editable Response',
    },
    {
      faIconClass: 'fa fa-exclamation-circle text-warning',
      infoText: 'Mandatory question to be answered',
    },
  ];

  private _logicExecutionStr: Subject<string> = new Subject();
  public get logicExecutionStr(): Observable<string> {
    return this._logicExecutionStr.asObservable();
  }
  public set logicExecutionStr(value: string) {
    this._logicExecutionStr.next(value);
  }

  private _currentlyPreviewedQue$: Subject<RenderPreview> = new Subject();
  public get currentlyPreviewedQue$(): Observable<RenderPreview> {
    return this._currentlyPreviewedQue$.asObservable();
  }
  public set currentlyPreviewedQue$(value: RenderPreview) {
    this._currentlyPreviewedQue$.next(value);
  }

  private _renderPreviewDataQue$: Subject<RenderPreview[]> = new Subject();
  public get renderPreviewDataQue$(): Observable<RenderPreview[]> {
    return this._renderPreviewDataQue$.asObservable();
  }
  public set renderPreviewDataQue$(value: RenderPreview[]) {
    this._renderPreviewDataQue$.next(value);
  }

  private _cqScore$: Subject<FormScore[]> = new Subject();
  public get cqScore$(): Observable<FormScore[]> {
    return this._cqScore$.asObservable();
  }
  public set cqScore$(value: FormScore[]) {
    this._cqScore$.next(value);
  }

  private _placeholderList: any[] = [];
  private _placeholderList$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  public get placeholderList$(): Observable<any[]> {
    return this._placeholderList$.asObservable();
  }
  public get placeholderList(): any[] {
    return this._placeholderList;
  }
  public set placeholderList(value: any[]) {
    this._placeholderList = [
      ...value
    ];
    this._placeholderList$.next(this._placeholderList);
  }

  public disablePreviousButton: boolean = false;
  public disableNextButton: boolean = false;
  public isLastNonSubmitterQnPreview: boolean = false;
  public isViewReady: boolean = false;
  public enableNavigation: boolean = false;
  public isMgmtPreview: boolean = false;
  public renderSubmitterPreview: boolean = false;

  public helper$: Subject<string> = new Subject();

  /**
   * Stores conditional logic effects to be applied when target questions are rendered.
   * Key: target element ID (e.g., "Dropdown_XXX")
   * Value: { value: string, disabled: boolean, clear: boolean }
   * - clear: true means this field should be cleared/reset when rendered
   */
  public conditionalEffects: Map<string, { value?: string; disabled: boolean; clear?: boolean; originalValue?: string; originalAnswerText?: string }> = new Map();

  public execute(logicToExecute: string) {
    this.logicExecutionStr = logicToExecute;
  }

  constructor(@Inject(WINDOW) private _window: Window) {}

  private _apiService = inject(ApiServiceService);
  private _httpClient = inject(HttpClient);

  // private _headers = new HttpHeaders()
  //   .set('Authorization', this._apiService.FormsAPIToken)
  //   .set('Access-Control-Allow-Origin', this._apiService.FormAPIAppID)
  //   .set('__RequestVerificationToken', this._apiService.FormsAPIToken);

  public getConditionalLogicJSON(templateId: string | number): Observable<any> {
    const apiUrl = `${this._apiService.dynamicEndPoint}/GetConditonalJson`;
    return this._httpClient.get<any>(
      `${apiUrl}/${templateId}`,
    ).pipe(
      map(response => this._apiService.convertTopLevelKeysToPascalCase(response))
    );
  }

  public getUserAnswers(surveyId: string | number): Observable<any> {
    const apiUrl = `${this._apiService.dynamicEndPoint}/GetUserResponse`;

    return this._httpClient.get<any>(
      `${apiUrl}?surveyId=${surveyId}`,
    ).pipe(
      map(response => this._apiService.convertKeysToPascalCase(response))
    );
  }

  public SaveUserSurveyAttachmentResponse(fileUploadObject: unknown): Observable<any> {
    const apiUrl = `${this._apiService.dynamicEndPoint}/SaveUserSurveyAttachmentResponse`;
    return this._httpClient.post<any>(
      `${apiUrl}`,
      fileUploadObject,
    ).pipe(
      map(response => this._apiService.convertKeysToPascalCase(response))
    );
  }

  public ProcessFileScan(fileUploadObject: unknown): Observable<any> {
    const apiUrl = `${this._apiService.dynamicEndPoint}/ProcessFileScan`;
    return this._httpClient.post<any>(
      `${apiUrl}`,
      fileUploadObject,
    ).pipe(
      map(response => this._apiService.convertKeysToPascalCase(response))
    );
  }

  public downloadFormQuestionAttachment(questionUniqueId: any, fileName: any, surveyRefID: any): Observable<Blob> {
    const apiUrl = `${this._apiService.dynamicEndPoint}/DownloadFormQuestionAttachment`;
    return this._httpClient.post(
      `${apiUrl}?questionUniqueId=${encodeURIComponent(questionUniqueId)}&fileName=${encodeURIComponent(fileName)}&surveyUniqueId=${encodeURIComponent(surveyRefID)}`,
      null,
      { responseType: 'blob' }
    );
  }

  public removeFormQuestionAttachment(questionUniqueId: any, fileName: any): Observable<any> {
    const apiUrl = `${this._apiService.dynamicEndPoint}/RemoveFormQuestionAttachment`;
    const reqObj ={
      questionUniqueId: questionUniqueId,
      fileName: fileName
    };
    return this._httpClient.post<any>(
      `${apiUrl}`,
      reqObj,
    ).pipe(
      map(response => this._apiService.convertKeysToPascalCase(response))
    );
  }
  
  saveFile(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    window.URL.revokeObjectURL(url);
  }

  public saveUserAnswers(userAnswersObj: unknown): Observable<any> {
    const apiUrl = `${this._apiService.dynamicEndPoint}/SaveUserSurveyResponse`;

    return this._httpClient.post<any>(
      `${apiUrl}`,
      userAnswersObj,
    ).pipe(
      map(response => this._apiService.convertKeysToPascalCase(response))
    );
  }

  public submitUserAnswers(userAnswersObj: unknown): Observable<any> {
    const apiUrl = `${this._apiService.dynamicEndPoint}/SubmitUserSurveyResponse`;
    
    return this._httpClient.post<any>(
      `${apiUrl}`,
      userAnswersObj,
    ).pipe(
      map(response => this._apiService.convertKeysToPascalCase(response))
    );
  }

  public getCQScoresByJSON(formData): Observable<any> {
    const apiUrl = `${this._apiService.dynamicEndPoint}/GetScoresforStandardForms`;

    // Response is consumed as camelCase (success/msg/data, compliance/risk/response,
    // label/score). Do NOT run convertKeysToPascalCase here or the keys flip to PascalCase.
    return this._httpClient.post<any>(
      `${apiUrl}`,
      formData,
    );
  }

  public getActiveConfigurations(): Observable<any> {
    const apiUrl = `${this._apiService.dynamicEndPoint}/GetActiveConfigurations?settingName=ActiveTag`;
    return this._httpClient.get<any>(`${apiUrl}`)
      .pipe(
        map(response => this._apiService.convertKeysToPascalCase(response))
      );
  }
}
